'use client';

import { memo, useState } from 'react';
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  useFormState,
  UseFormTrigger,
} from 'react-hook-form';
import NextButton from '@/components/Button/NextButton';
import DOBInput from '@/components/Input/DOBInput';
import { FormData, FormVariant } from '@/types/form';
import { fieldRules } from '@/utils/form';
import { WarningIcon } from '@/assets/icons/WarningIcon';
import generalStyles from '../FormBody.module.scss';
import useModal from '@/hooks/useModal';
import styles from './StepTen.module.scss';

interface Props {
  formData?: FormData;
  register?: UseFormRegister<FormData>;
  handleSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<FormData, any, FormData>;
  isModal?: boolean;
  trigger?: UseFormTrigger<FormData>;
  setValue?: UseFormSetValue<FormData>;
  stepLoading?: boolean;
  verificationError?: boolean;
  variant?: FormVariant;
}

const DOBForm = ({
  formData,
  handleSubmit,
  register,
  control,
  isModal,
  trigger,
  setValue,
  stepLoading = false,
  verificationError = false,
  variant,
}: Props) => {
  const { closeModal } = useModal();
  const { errors } = useFormState({ control });
  const [focused, setFocused] = useState(variant !== 'reapply');

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isValid = await trigger?.();
    if (isValid) {
      await handleSubmit?.();
      if (isModal) {
        closeModal();
      }
    }
  };

  return (
    <form onSubmit={submitForm} className={styles.form}>
      {(!isModal || focused) && register && (
        <DOBInput
          currentValue={formData?.date_of_birth || ''}
          label="Date of birth"
          placeholder="MM/DD/YYYY"
          register={register('date_of_birth', fieldRules.date_of_birth)}
          error={errors?.date_of_birth}
          id="date_of_birth"
        />
      )}

      {isModal && !focused && (
        <div
          onClick={() => {
            setFocused(true);
            setValue?.('date_of_birth', '');
          }}
          className={styles.fakeInputWrapper}
        >
          <p className={styles.fakeLabel}>Date of birth</p>
          <div className={styles.fakeInput}>
            {formData ? `**/**/${formData.date_of_birth.slice(-4)}` : ''}
          </div>
        </div>
      )}
      <NextButton
        buttonType="submit"
        type="button"
        text={isModal ? 'Apply changes' : 'Next step'}
        styles={`${generalStyles.button} ${isModal ? generalStyles.smallButton : ''}`}
        isArrow={!isModal}
        loading={stepLoading}
        disabled={stepLoading}
      />
      {verificationError && (
        <p
          className={`${generalStyles.error} ${generalStyles.verificationError}`}
        >
          <WarningIcon className={generalStyles.warningIcon} />
          <span>We couldn&apos;t verify that you are human</span>
        </p>
      )}
    </form>
  );
};

export default memo(DOBForm);
